import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Search,
  ChevronDown,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Filter,
  Users,
  Check,
} from "lucide-react";
import { DashboardService } from "../../services/dashboard";

const CustomSelect = ({ value, onChange, options, icon: Icon, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative min-w-[220px]" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-3 bg-gray-50/50 border ${isOpen ? "border-[#881337]/30 ring-4 ring-[#881337]/5 bg-white" : "border-gray-100/80"
          } rounded-xl py-2.5 px-4 text-[12px] font-bold text-gray-700 transition-all outline-none text-left`}
      >
        <div className="text-gray-400">
          <Icon size={14} />
        </div>
        <span className="flex-1 truncate">
          {value || placeholder}
        </span>
        <ChevronDown
          size={14}
          className={`text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-[100] mt-2 w-full bg-white border border-gray-100 rounded-[1.25rem] shadow-xl shadow-gray-200/40 py-2.5 animate-in fade-in zoom-in duration-300 overflow-hidden">
          {options.map((option) => (
            <button
              key={option}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className={`w-full flex items-center justify-between px-4 py-2 text-[11px] font-bold transition-all ${value === option
                ? "bg-[#881337]/5 text-[#881337]"
                : "text-gray-500 hover:bg-gray-50 hover:text-[#881337]"
                }`}
            >
              <span>{option}</span>
              {value === option && (
                <div className="w-5 h-5 rounded-full bg-[#881337] flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PlatformOverview = ({ data, loading }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activityFilter, setActivityFilter] = useState("All Activity Levels");
  const [completionFilter, setCompletionFilter] = useState(
    "All Completion Rates"
  );
  const [allDirectors, setAllDirectors] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isOpenRows, setIsOpenRows] = useState(false);
  const dropdownRef = useRef(null);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpenRows(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    const fetchDirectors = async () => {
      try {
        setLoadingData(true);
        const data = await DashboardService.getAllDirectors(1, 1000);
        setAllDirectors(data?.data || []);
        setLoadingData(false);
      } catch (error) {
        setLoadingData(false);
        console.error("Error fetching directors:", error);
      }
    };
    fetchDirectors();
  }, []);

  const filteredDirectors = useMemo(() => {
    let result = [...allDirectors];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u =>
        u.first_name?.toLowerCase().includes(term) ||
        u.last_name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term)
      );
    }

    if (activityFilter && activityFilter !== "All Activity Levels") {
      if (activityFilter.includes("High")) {
        result = result.filter(u => u.restaurantActivityinPercentage > 80);
      } else if (activityFilter.includes("Medium")) {
        result = result.filter(u => u.restaurantActivityinPercentage >= 50 && u.restaurantActivityinPercentage <= 80);
      } else if (activityFilter.includes("Low")) {
        result = result.filter(u => u.restaurantActivityinPercentage < 50);
      }
    }

    if (completionFilter && completionFilter !== "All Completion Rates") {
      if (completionFilter.includes("> 80%")) {
        result = result.filter(u => u.avgCompletionRate > 80);
      } else if (completionFilter.includes("50-80%")) {
        result = result.filter(u => u.avgCompletionRate >= 50 && u.avgCompletionRate <= 80);
      } else if (completionFilter.includes("< 50%")) {
        result = result.filter(u => u.avgCompletionRate < 50);
      }
    }

    // Apply Sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'name':
            aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
            bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
            break;
          case 'activity':
            aValue = a.restaurantActivityinPercentage || 0;
            bValue = b.restaurantActivityinPercentage || 0;
            break;
          case 'employees':
            aValue = a.totalEmployeesinTraining || 0;
            bValue = b.totalEmployeesinTraining || 0;
            break;
          case 'completion':
            aValue = a.avgCompletionRate || 0;
            bValue = b.avgCompletionRate || 0;
            break;
          case 'login':
            aValue = a.last_login ? new Date(a.last_login).getTime() : 0;
            bValue = b.last_login ? new Date(b.last_login).getTime() : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [allDirectors, searchTerm, activityFilter, completionFilter, sortConfig]);

  const displayedDirectors = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredDirectors.slice(start, start + rowsPerPage);
  }, [filteredDirectors, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredDirectors.length / rowsPerPage) || 1;

  const handleFilterChange = (setter, value) => {
    setter(value);
    setCurrentPage(1);
  };

  const getProgressColor = (value) => {
    if (value >= 80) return "bg-emerald-500";
    if (value >= 50) return "bg-amber-500";
    return "bg-rose-500";
  };

  const getTextColor = (value) => {
    if (value >= 80) return "text-emerald-600";
    if (value >= 50) return "text-amber-600";
    return "text-rose-600";
  };

  const clearFilters = () => {
    setSearchTerm("");
    setActivityFilter("All Activity Levels");
    setCompletionFilter("All Completion Rates");
    setSortConfig({ key: null, direction: 'asc' });
    setCurrentPage(1);
  };

  if (loadingData) {
    return (
      <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 animate-pulse">
        <div className="h-8 bg-gray-100 rounded w-1/4 mb-8"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-gray-50 rounded w-full"></div>
          ))}
        </div>
      </div>
    );
  }

  const formatRelativeTime = (date) => {
    if (!date) return "-";

    const now = new Date();
    const past = new Date(date);

    const diffMs = now - past;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return "Just now";
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

    return past.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const columns = [
    { label: "Director Name", key: "name", align: "left" },
    { label: "Restaurants Active (%)", key: "activity", align: "left" },
    { label: "Total Employees in Training", key: "employees", align: "center" },
    { label: "Avg. Completion %", key: "completion", align: "center" },
    { label: "Last Login (Director)", key: "login", align: "right" },
  ];

  return (
    <div className="bg-white rounded-[24px] p-6 lg:p-8 shadow-sm border border-gray-100 mb-8 font-sans">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4">
        <h2 className="text-[22px] font-black text-[#111827] tracking-tight">
          Platform Overview
        </h2>
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsOpenRows(!isOpenRows)}
            className="flex items-center gap-3 bg-gray-50/80 px-4 py-2.5 rounded-xl border border-gray-100 shadow-sm hover:bg-white transition-all group"
          >
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#881337]"></div>
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest group-hover:text-gray-600">
                Show:
              </span>
            </div>
            <span className="text-[11px] font-black text-[#881337]">
              {rowsPerPage} rows
            </span>
            <ChevronDown
              size={12}
              className={`text-[#881337] transition-transform duration-300 ${isOpenRows ? 'rotate-180' : ''}`}
            />
          </button>

          {isOpenRows && (
            <div className="absolute right-0 top-full mt-2 w-[140px] bg-white border border-gray-100 rounded-[1.25rem] shadow-xl shadow-gray-200/40 py-2.5 z-50 overflow-hidden animate-in fade-in zoom-in duration-300">
              {[5, 10, 20, 50].map((val) => (
                <button
                  key={val}
                  onClick={() => {
                    setRowsPerPage(val);
                    setIsOpenRows(false);
                    setCurrentPage(1);
                  }}
                  className={`w-full text-left px-4 py-2 text-[10px] font-black uppercase tracking-wider transition-all ${rowsPerPage === val
                    ? "bg-[#881337] text-white"
                    : "text-gray-400 hover:bg-gray-50 hover:text-[#881337]"
                    }`}
                >
                  {val} rows
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-10">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300"
          />
          <input
            type="text"
            placeholder="Search director..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full bg-gray-50/50 border border-gray-100/80 rounded-xl py-2.5 pl-11 pr-4 text-[12px] font-medium focus:ring-4 focus:ring-[#881337]/5 focus:border-[#881337]/20 focus:bg-white transition-all outline-none placeholder:text-gray-400 placeholder:font-normal"
          />
        </div>

        <CustomSelect
          value={activityFilter}
          onChange={(val) => handleFilterChange(setActivityFilter, val)}
          options={["All Activity Levels", "High Activity", "Medium Activity", "Low Activity"]}
          icon={Users}
          placeholder="Activity Level"
        />

        <CustomSelect
          value={completionFilter}
          onChange={(val) => handleFilterChange(setCompletionFilter, val)}
          options={[
            "All Completion Rates",
            "High ( > 80% )",
            "Medium ( 50-80% )",
            "Low ( < 50% )",
          ]}
          icon={Filter}
          placeholder="Completion Rate"
        />

        <button
          onClick={clearFilters}
          className="flex items-center gap-2 bg-[#FFF1F2] text-[#881337] px-6 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-[#FFE4E6] transition-all"
        >
          <RotateCcw size={14} className="text-[#E11D48]" />
          Clear Filters
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr className="border-b border-gray-50">
              {columns.map((column, i) => (
                <th
                  key={i}
                  className={`px-4 py-4 ${column.align === "center"
                    ? "text-center"
                    : column.align === "right"
                      ? "text-right"
                      : "text-left"
                    }`}
                >
                  <button
                    onClick={() => handleSort(column.key)}
                    className="inline-flex items-center gap-4 group"
                  >
                    <span className={`text-[11px] font-bold uppercase tracking-widest whitespace-nowrap transition-colors ${sortConfig.key === column.key ? 'text-[#881337]' : 'text-gray-400 group-hover:text-gray-600'}`}>
                      {column.label}
                    </span>
                    <ArrowUpDown
                      size={12}
                      className={`transition-colors ${sortConfig.key === column.key ? 'text-[#881337]' : 'text-gray-300 group-hover:text-gray-500'}`}
                    />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {displayedDirectors.map((item, idx) => {
              const totalRestaurantsOwner = item.assigned_restaurants.length;
              const activeCount = item.assigned_restaurants.filter(
                (r) => r.status === "active"
              ).length;
              const activePerc = item?.restaurantActivityinPercentage;
              const totalEmps = item?.totalEmployeesinTraining;
              const avgComp = item?.avgCompletionRate;
              return (
                <tr key={idx} className="hover:bg-gray-50/40 transition-colors">
                  <td className="px-4 py-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full border-2 border-white shadow-sm overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center font-black text-gray-400 text-sm">
                        {item.avatar || item.logo ? (
                          <img
                            src={item.avatar || item.logo}
                            alt="avatar"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          `${item.first_name?.[0] || ""}${item.last_name?.[0] || ""
                          }`
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#111827] leading-none mb-1">
                          {item.first_name} {item.last_name}
                        </p>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                          {totalRestaurantsOwner} Restaurants
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-5">
                    <div className="flex items-center gap-3 min-w-[160px]">
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                        <div
                          className={`h-full rounded-full transition-all duration-1000 ${getProgressColor(
                            activePerc
                          )}`}
                          style={{ width: `${activePerc}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-black text-[#111827] w-8">
                        {activePerc}%
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-5 text-center text-sm font-black text-[#111827]">
                    {totalEmps}
                  </td>
                  <td
                    className={`px-4 py-5 text-center text-sm font-black ${getTextColor(
                      avgComp
                    )}`}
                  >
                    {avgComp}%
                  </td>
                  <td title={new Date(item.last_login).toLocaleString()}>
                    {formatRelativeTime(item.last_login)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mt-8 pt-8 border-t border-gray-50">
        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em]">
          Showing <span className="text-[#881337]">{currentPage}</span>{" "}
          of <span className="text-[#881337]">{totalPages}</span>{" "}
          results
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-100 text-[11px] font-black uppercase tracking-widest text-[#111827] hover:bg-gray-50 transition-all disabled:opacity-30 disabled:hover:bg-transparent shadow-sm"
          >
            <ChevronLeft size={16} />
            Previous
          </button>
          <button
            onClick={() =>
              setCurrentPage((p) => Math.min(totalPages || 1, p + 1))
            }
            disabled={currentPage === totalPages}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-100 text-[11px] font-black uppercase tracking-widest text-[#111827] hover:bg-gray-50 transition-all disabled:opacity-30 disabled:hover:bg-transparent shadow-sm"
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlatformOverview;
